<!-- Chat form for selecting multiple checkbox options, including custom and "none" options. -->

<script lang="ts" setup>
import { Form } from '@nzyme/vue-forms/components/Form.jsx';
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';
import { useEmitAsync } from '@nzyme/vue-utils/useEmitAsync.js';
import { Reveal } from '@nzyme/vue/components/Reveal.jsx';
import { Plus } from 'lucide-vue-next';
import { ref } from 'vue';

import type { MultiSelectInputConfig, MultiSelectInputValue } from '@sigma/common/forms/MultiSelectInput.js';

import Button from '../../Button.vue';
import Checkbox from '../../forms/Checkbox.vue';
import type { CheckboxEditableValue } from '../../forms/CheckboxEditable.js';
import CheckboxEditable from '../../forms/CheckboxEditable.vue';
import FieldInfo from '../../forms/FieldInfo.vue';
import * as l from './ChatMultiSelectInput.loc.js';

interface Props {
  config: MultiSelectInputConfig;
}

interface Emits {
  submit: [value: MultiSelectInputValue];
}

defineProps<Props>();
defineEmits<Emits>();

const t = useTranslate();
const emitAsync = useEmitAsync<Emits>();

const error = ref<string | null>(null);

// Local state to track if "none" option is selected
const noneSelected = ref(false);

// Local state for selected predefined options
const selectedOptions = ref<string[]>([]);

// Local state for custom options (with checked state and text)
const customOptions = ref<CheckboxEditableValue[]>([]);

// Track index of the last added custom option for auto-focus
const lastAddedCustomIndex = ref<number | null>(null);

function isValueSelected(value: string | undefined) {
  if (!value || noneSelected.value) {
    return false;
  }

  return selectedOptions.value.includes(value);
}

function updateValueSelected(value: string | undefined, selected: boolean) {
  if (!value) {
    return;
  }

  error.value = null;

  // Clear none selection when selecting an option
  noneSelected.value = false;

  const current = selectedOptions.value;

  if (!selected) {
    selectedOptions.value = current.filter(v => v !== value);
  } else if (!current.includes(value)) {
    selectedOptions.value = [...current, value];
  }
}

function updateNoneSelected(selected: boolean) {
  error.value = null;
  noneSelected.value = selected;

  if (selected) {
    // Clear selections when none is selected
    selectedOptions.value = [];
    // Uncheck all custom options (but keep them)
    customOptions.value = customOptions.value.map(opt => ({ ...opt, checked: false }));
  }
}

function addCustomOption() {
  error.value = null;

  // Clear none selection when adding custom option
  noneSelected.value = false;

  lastAddedCustomIndex.value = customOptions.value.length;
  customOptions.value = [...customOptions.value, { checked: true, text: '' }];
}

function updateCustomOption(index: number, value: CheckboxEditableValue) {
  const newOptions = [...customOptions.value];
  newOptions[index] = value;
  customOptions.value = newOptions;

  if (value.checked) {
    noneSelected.value = false;
  }

  // Clear the last added index after the first update
  if (lastAddedCustomIndex.value === index) {
    lastAddedCustomIndex.value = null;
  }
}

async function handleSubmit() {
  error.value = null;

  // If none is selected, emit empty array
  if (noneSelected.value) {
    await emitAsync('submit', { type: 'MULTI_SELECT', options: [] });
    return;
  }

  // Filter custom options: only include checked ones with non-empty text
  const validCustomOptions = customOptions.value
    .filter(opt => opt.checked && opt.text.trim())
    .map(opt => opt.text.trim());

  const allSelectedOptions = [...selectedOptions.value, ...validCustomOptions];

  if (allSelectedOptions.length === 0) {
    error.value = t(l.selectOption);
    return;
  }

  await emitAsync('submit', { type: 'MULTI_SELECT', options: allSelectedOptions });
}
</script>

<template>
  <Form
    :class="css.optionsContainer"
    @submit="handleSubmit"
  >
    <div :class="css.options">
      <Checkbox
        v-for="(option, index) in config.options"
        :key="index"
        :tid="'option-' + index"
        :inner-class="css.checkboxInner"
        :class="[css.checkbox, isValueSelected(option) && css.selected]"
        :model-value="isValueSelected(option)"
        :content-class="css.checkboxContent"
        @update:model-value="updateValueSelected(option, $event)"
      >
        {{ option }}
      </Checkbox>

      <CheckboxEditable
        v-for="(option, index) in customOptions"
        :key="'custom-' + index"
        :input-class="css.checkboxInput"
        :class="[css.checkbox, option.checked && css.selected]"
        :model-value="option"
        :placeholder="config.customOption"
        :focus="lastAddedCustomIndex === index"
        @update:model-value="updateCustomOption(index, $event)"
      />

      <Reveal v-if="config.customOption">
        <button
          type="button"
          :class="css.addCustomButton"
          @click="addCustomOption"
        >
          <Plus :class="css.icon" />
          {{ config.customOption }}
        </button>
      </Reveal>

      <Checkbox
        v-if="config.noneOption"
        :class="[css.checkbox, noneSelected && css.selected]"
        :model-value="noneSelected"
        :content-class="css.checkboxContent"
        @update:model-value="updateNoneSelected($event)"
      >
        {{ config.noneOption }}
      </Checkbox>
    </div>

    <FieldInfo
      v-if="error"
      type="error"
    >
      {{ error }}
    </FieldInfo>

    <Button
      tid="chat-question-submit"
      variant="primary"
      size="large"
      color="black"
      rounded
      :class="css.submitButton"
      type="submit"
    >
      {{ t(l.submitButton) }}
    </Button>
  </Form>
</template>

<style lang="scss" module="css">
.optionsContainer {
  display: flex;
  flex-direction: column;
  gap: $spacing-08;
}

.options {
  display: flex;
  flex-direction: column;
  gap: $spacing-08;
}

.checkbox {
  border-radius: $border-radius-06;
  border: 1px solid $color-doctor-100;
  background: transparent;
  padding: $spacing-12;
  transition:
    border-color 0.2s ease-out,
    background-color 0.2s ease-out;

  &:hover {
    border-color: $color-doctor-200;
    background-color: $color-doctor-50;
  }

  &:focus-within {
    outline: 2px solid $color-black;
    outline-offset: 2px;
  }

  &.selected {
    border-color: $color-doctor-300;
    background-color: $color-doctor-50;
  }
}

.checkboxInput {
  padding: $spacing-12 $spacing-12 $spacing-12 0;
  margin: -1 * $spacing-12 -1 * $spacing-12 -1 * $spacing-12 0;
}

.addCustomButton {
  display: flex;
  align-items: center;
  gap: $spacing-08;
  padding: $spacing-12;
  border-radius: $border-radius-06;
  border: 1px dashed $color-doctor-200;
  background: transparent;
  color: $color-gray-600;
  cursor: pointer;
  transition:
    border-color 0.2s ease-out,
    background-color 0.2s ease-out,
    color 0.2s ease-out;

  &:hover {
    border-color: $color-doctor-600;
    background-color: $color-doctor-50;
    color: $color-text;
  }

  &:disabled {
    opacity: 0.6;
    pointer-events: none;
  }
}

.icon {
  width: 1em;
  height: 1em;
}

.submitButton {
  margin-top: $spacing-08;
}
</style>
